import subscriptionData from '../assets/data/subscription-donation'
import transactionData from '../assets/data/transaction-donation'
import { RootObject } from '../types/InAppDonationTypes/comcastDonationTypes'
import { ConsentParams } from '../types/InAppDonationTypes/consentParamsTypes'
import { SubscriptionDataPayload } from '../types/InAppDonationTypes/subscriptionDonationTypes'
import { TransactionDataPayload } from '../types/InAppDonationTypes/transactionDonationTypes'
import { MONTHLY } from './ComcastBilling'
import { store } from '../store'
import {
  UrgentMessage,
  UrgentMessageKeys,
} from '../components/UrgentMessageModal/types'
import PBSError from '../../../shared/PBSError'

type TDonationInformation = {
  donationCampaignId: string
  totalChargeAmount: number
  consentToken: string
  oat: string
  donationFrequency: string
}

const DEFAULT_MONTHS = 12

export const getDonationPayload = (
  consentParams: ConsentParams,
  donationInformation: TDonationInformation
): RootObject<SubscriptionDataPayload> | RootObject<TransactionDataPayload> => {
  const { user } = store.getState()
  const {
    donationCampaignId,
    totalChargeAmount,
    consentToken,
    oat,
    donationFrequency,
  } = donationInformation

  const payload:
    | RootObject<SubscriptionDataPayload>
    | RootObject<TransactionDataPayload> =
    donationFrequency === MONTHLY ? subscriptionData : transactionData

  payload.data.relationships.payment_campaign.data.id = donationCampaignId
  payload.data.attributes.gateway_transaction_request =
    consentParams.productDetails

  payload.data.attributes.amount = totalChargeAmount * 100
  payload.data.relationships.customer.data.first_name = user.first_name
  payload.data.relationships.customer.data.last_name = user.last_name
  payload.data.relationships.customer.data.email = user.email
  payload.data.relationships.customer.data.pid = user.pid
  payload.data.attributes.token = consentToken
  payload.data.relationships.customer.data.gateway_customer_id = oat

  if (donationFrequency === MONTHLY) {
    const subscriptionDataPayload =
      payload as RootObject<SubscriptionDataPayload>
    subscriptionDataPayload.data.attributes.months = DEFAULT_MONTHS
  }

  return payload
}
export const formatAmount = (amount: number): string =>
  (+amount / 100).toFixed(2)

export const hasNoNullishValues = (object: Record<string, any>): boolean => {
  for (const value of Object.values(object)) {
    if (value === null || value === undefined || value === '') {
      return false
    }
  }
  return true
}

type TObjectValidationInformation = {
  isValid: boolean
  invalidFields: UrgentMessageKeys[]
}

export const validateResponseObject = (
  response: Partial<UrgentMessage>
): TObjectValidationInformation => {
  const validationResult = {
    isValid: true,
    invalidFields: [] as UrgentMessageKeys[],
  }

  if (Object.keys(response).length > 0) {
    const requiredKeys = Object.values(UrgentMessageKeys)

    requiredKeys.forEach((key) => {
      if (
        response[key] === null ||
        response[key] === undefined ||
        (typeof response[key] === 'string' && response[key].trim() === '')
      ) {
        validationResult.isValid = false
        validationResult.invalidFields.push(key)
      }
    })
    return validationResult
  } else {
    return {
      isValid: false,
      invalidFields: [],
    }
  }
}

const enum Environment {
  PROD = 'prod',
  STAGING = 'staging',
  QA = 'qa',
  DEV = 'edcar',
  LOCALHOST = 'localhost',
}

export const checkEnvironmentFlags = (
  urgentMessage: UrgentMessage
): boolean => {
  const environmentChecks = [
    {
      key: UrgentMessageKeys.SHOW_MESSAGE_IN_PROD,
      check: () => checkEnvironment(Environment.PROD),
    },
    {
      key: UrgentMessageKeys.SHOW_MESSAGE_IN_STAGING,
      check: () => checkEnvironment(Environment.STAGING),
    },
    {
      key: UrgentMessageKeys.SHOW_MESSAGE_IN_DEV,
      check: () =>
        checkEnvironment(Environment.DEV) ||
        checkEnvironment(Environment.LOCALHOST) ||
        checkEnvironment(Environment.QA),
    },
  ]

  return environmentChecks.some(
    ({ key, check }) => urgentMessage[key] && check()
  )
}

const checkEnvironment = (environment: Environment): boolean => {
  const currentEnvironment = window.location.hostname
  return currentEnvironment.includes(environment)
}

export const getErrorDescriptionDetails = (
  e: PBSError
): {
  errorText: string
  errorCodePrefix: string
  errorDetails: string
} => {
  const { errorMessage } = e.parse()
  const isTimeout = e.errorStatus === 408
  const REQUEST_TIMEOUT_MESSAGE = 'Request Timed Out'
  const errorTimeoutMessage = isTimeout
    ? e.timeOutMessage || REQUEST_TIMEOUT_MESSAGE
    : ''
  const errorDetails = isTimeout
    ? ' - ' + errorTimeoutMessage
    : e.errorStatus + ' - ' + errorMessage.errorTextCode

  return {
    errorText: errorMessage.errorText,
    errorCodePrefix: e.errorCodePrefix,
    errorDetails,
  }
}
